"use client";

import Pagination from "@/components/common/Pagination";
import { useQuery } from "@/hooks/useQuery";
import { ApiResponse } from "@/interfaces/api-response";
import { Filter } from "@/interfaces/filter.interface";
import useFilterStore, { FilterQuery } from "@/stores/order-filter.store";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { DataTable, DataTableProps } from "primereact/datatable";
import React, { useEffect, useImperativeHandle, useMemo, useRef } from "react";
import DatePickerBox from "./DatePickerBox";
import FilterBox from "./FilterBox";
import SearchBox from "./SearchBox";
import FilterSidebar from "./FilterSidebar";

interface DataTableEnhancementsProps<T> {
  cachedKey?: string;
  fetchData: (props: {
    page: number;
    size: number;
    search?: string;
    filters?: Partial<FilterQuery>;
  }) => Promise<ApiResponse<T[]>>;
  itemsPerPage?: number;
  refetchRef?: React.RefObject<(() => void) | null>;
  searchPlaceholder?: string;
  title?: string;
  filterOptions?: Record<string, Filter[]>;
  dateConfig?: {
    fields: [string, string];
    defaultValues: [Date, Date];
  };
  suffix?: string | React.ReactNode;
}

const withDataTableEnhancements = <T extends Record<string, unknown>>(
  WrappedDataTable: React.ComponentType<DataTableProps<T[]>>,
) => {
  return function EnhancedTable({
    cachedKey = "data",
    fetchData,
    itemsPerPage = 10,
    searchPlaceholder = "Search",
    title = "Data List",
    filterOptions = {},
    dateConfig,
    refetchRef,
    suffix,
    ...restProps
  }: DataTableEnhancementsProps<T> & DataTableProps<T[]>) {
    const globalFilter = useFilterStore((s) => s.filter);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const tableRef = useRef<HTMLDivElement>(null);
    const page = useMemo(() => {
      return globalFilter.page || 1;
    }, [globalFilter]);

    useEffect(() => {
      const params = new URLSearchParams();
      Object.entries(globalFilter).forEach(([key, val]) => {
        if (val == null) {
          params.delete(key);
        } else {
          params.set(key, String(val));
        }
      });

      window.history.pushState({}, "", `?${params.toString()}`);
    }, [globalFilter, pathname, router, searchParams]);

    const {
      data: response,
      loading,
      refetch: refetchData,
    } = useQuery<ApiResponse<T[]>>(
      [cachedKey, page, JSON.stringify(globalFilter)],
      async () => {
        const { search = "", ...filters } = globalFilter;
        return await fetchData({
          page,
          size: itemsPerPage,
          search,
          filters,
        });
      },
      {
        cacheTime: 60000,
        enabled: Object.keys(globalFilter).length > 0,
      },
    );

    const countData = response?.total || 0;

    useImperativeHandle(refetchRef, () => refetchData);
    useEffect(() => {
      if (tableRef.current) {
        tableRef.current.scrollTop = 0;
      }
    }, [response, loading]);
    const hasToolbar =
      Boolean(title) ||
      Boolean(searchPlaceholder) ||
      Boolean(dateConfig) ||
      Object.keys(filterOptions).length > 0 ||
      Boolean(suffix);

    return (
      <div className="flex h-full flex-col gap-4">
        {hasToolbar && (
          <div className="flex flex-col flex-wrap items-start justify-between gap-4 md:flex-row md:items-center">
            <h1 className="text-xl font-bold text-gray-800">{title}</h1>
            <div className="flex w-full flex-wrap items-center gap-4 md:w-auto">
              <i
                className="pi pi-sync cursor-pointer text-gray-500"
                onClick={() => {
                  refetchData();
                }}
              ></i>
              {searchPlaceholder && (
                <SearchBox searchPlaceholder={searchPlaceholder} />
              )}
              {dateConfig && (
                <DatePickerBox
                  fields={dateConfig.fields}
                  defaultValues={dateConfig.defaultValues}
                />
              )}
              {/* {filterOptions && Object.keys(filterOptions).length > 0 && (
                <FilterBox filterOptions={filterOptions} />
              )} */}
              {filterOptions && Object.keys(filterOptions).length > 0 && (
                <FilterSidebar filterOptions={filterOptions} />
              )}
              {suffix}
            </div>
          </div>
        )}
        <div
          className={`relative min-h-0 flex-1 ${
            loading ? "overflow-y-hidden opacity-50" : ""
          }`}
          ref={tableRef}
        >
          <WrappedDataTable
            rowHover
            value={response?.data as T[]}
            emptyMessage={
              <div className="flex h-40 w-full items-center justify-center text-gray-500">
                {!loading && <span>No data available</span>}
              </div>
            }
            className="w-full"
            size="small"
            scrollable
            scrollHeight="flex"
            {...restProps}
          />
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#ffffff18]">
              <i className="pi pi-spin pi-spinner text-2xl text-gray-800"></i>
            </div>
          )}
        </div>
        <Pagination totalItems={countData} itemsPerPage={itemsPerPage} />
      </div>
    );
  };
};

const EnhancedTable = withDataTableEnhancements(DataTable);

export default EnhancedTable;
